import { Component, EventEmitter, Input, OnInit, Output } from '@angular/core';
import { CommonModule } from '@angular/common';
import { TranslatePipe } from '../../../pipes/translate.pipe';
import { FormsModule } from '@angular/forms';
import { Loading } from '../../loader/loading/loading';
import * as XLSX from 'xlsx';
import * as mammoth from 'mammoth';

@Component({
  selector: 'app-standard-table',
  standalone: true,
  imports: [CommonModule, FormsModule, Loading, TranslatePipe],
  templateUrl: './standard-table.html',
  styleUrl: './standard-table.css',
})
export class StandardTable implements OnInit {
  @Input() title: string = 'Ticket List';
  @Input() columns: any[] = [];
  @Input() data: any[] = [];
  @Input() totalItems: number = 0;
  @Input() isLoading: boolean = false;
  @Input() pageSize: number = 10;
  @Input() currentPage: number = 1;

  @Input() pageSizeOptions: number[] = [10, 20, 50, 100];
  @Output() pageSizeChange = new EventEmitter<number>();
  @Output() searchChange = new EventEmitter<string>();
  @Output() sortChange = new EventEmitter<any>();
  @Output() pageChange = new EventEmitter<any>();
  @Output() rowClick = new EventEmitter<any>();
  @Input() isCreate: boolean = true;
  @Input() isEditOnly: boolean = false;
  @Input() isImport: boolean = false;
  @Input() isExport: boolean = false;
  @Input() showDateFilter: boolean = false;
  @Input() showStatusFilter: boolean = false;
  @Input() showAlphabetFilter: boolean = false;
  @Input() alphabetOptions: string[] = [];
  @Input() showSearch: boolean = true;
  @Input() showDateRangeFilter: boolean = false;
  
  @Output() dateFilterChange = new EventEmitter<{year: string, month: string}>();
  @Output() statusFilterChange = new EventEmitter<string>();
  @Output() alphabetFilterChange = new EventEmitter<string>();
  @Output() dateRangeChange = new EventEmitter<string>();

  @Output() create = new EventEmitter<void>();
  @Output() edit = new EventEmitter<any>();
  @Output() delete = new EventEmitter<any>();
  @Output() import = new EventEmitter<any[]>();
  @Output() export = new EventEmitter<void>();
  @Output() imageClick = new EventEmitter<string>();

  searchValue = '';
  
  isPreviewOpen = false;
  previewUrl = '';

  isDeleteConfirmOpen = false;
  pendingDeleteRow: any = null;

  onImageClick(event: Event, imageUrl: string) {
    event.stopPropagation();
    this.previewUrl = imageUrl;
    this.isPreviewOpen = true;
    this.imageClick.emit(imageUrl);
  }

  closePreview() {
    this.isPreviewOpen = false;
    this.previewUrl = '';
  }
  
  filterYear: string = '';
  filterMonth: string = '';
  filterStatus: string = '';
  filterAlphabet: string = '';
  filterDateRange: string = '';

  years: string[] = [];
  months: {value: string, name: string}[] = [
    {value: '01', name: 'January'},
    {value: '02', name: 'February'},
    {value: '03', name: 'March'},
    {value: '04', name: 'April'},
    {value: '05', name: 'May'},
    {value: '06', name: 'June'},
    {value: '07', name: 'July'},
    {value: '08', name: 'August'},
    {value: '09', name: 'September'},
    {value: '10', name: 'October'},
    {value: '11', name: 'November'},
    {value: '12', name: 'December'}
  ];

  statusOptions = [
    { value: '01', name: 'Open' },
    { value: '02', name: 'Confirmed' },
    { value: '03', name: 'Failed' },

  ];

  dateRangeOptions = [
    { value: 'today', name: 'Today' },
    { value: 'yesterday', name: 'Yesterday' },
    { value: 'week', name: 'This Week' },
    { value: 'month', name: 'This Month' }
  ];

  constructor() {}

  ngOnInit(): void {
    const now = new Date();
    const currentYear = now.getFullYear();
    const currentMonth = (now.getMonth() + 1).toString().padStart(2, '0');
    
    this.filterYear = currentYear.toString();
    this.filterMonth = currentMonth;

    for (let i = 0; i < 10; i++) {
        this.years.push((currentYear - i).toString());
    }
  }

  onSearch() {
    this.searchChange.emit(this.searchValue);
  }

  onDateFilterChange() {
    this.dateFilterChange.emit({year: this.filterYear, month: this.filterMonth});
  }

  onStatusFilterChange() {
    this.statusFilterChange.emit(this.filterStatus);
  }

  onAlphabetFilterChange() {
    this.alphabetFilterChange.emit(this.filterAlphabet);
  }

  onDateRangeChange() {
    this.dateRangeChange.emit(this.filterDateRange);
  }

  onSort(column: any) {
    this.sortChange.emit(column);
  }

  onPageChange(page: number) {
    if (page >= 1 && page <= this.totalPages && page !== this.currentPage) {
      this.pageChange.emit(page);
    }
  }

  onPageSizeChange(event: Event) {
    const target = event.target as HTMLSelectElement;
    this.pageSizeChange.emit(Number(target.value));
  }

  onRowClick(row: any) {
    this.rowClick.emit(row);
  }

  onCreate() {
    this.create.emit();
  }

  onEdit(event: Event, row: any) {
    event.stopPropagation();
    this.edit.emit(row);
  }

  onImport() {
    console.log('Import triggered');
  }

  onExport() {
    this.export.emit();
  }

  onDelete(event: Event, row: any) {
    event.stopPropagation();
    this.pendingDeleteRow = row;
    this.isDeleteConfirmOpen = true;
  }

  confirmDelete() {
    if (this.pendingDeleteRow) {
      this.delete.emit(this.pendingDeleteRow);
    }
    this.isDeleteConfirmOpen = false;
    this.pendingDeleteRow = null;
  }

  cancelDelete() {
    this.isDeleteConfirmOpen = false;
    this.pendingDeleteRow = null;
  }

  onFileChange(event: any) {
    const target: DataTransfer = <DataTransfer>event.target;
    if (target.files.length !== 1) return;

    const file = target.files[0];
    const fileExtension = file.name.split('.').pop()?.toLowerCase();

    if (fileExtension === 'xlsx' || fileExtension === 'xls') {
      const reader: FileReader = new FileReader();
      reader.onload = (e: any) => {
        const bstr: string = e.target.result;
        const wb: XLSX.WorkBook = XLSX.read(bstr, { type: 'binary' });
        const wsname: string = wb.SheetNames[0];
        const ws: XLSX.WorkSheet = wb.Sheets[wsname];
        const data = XLSX.utils.sheet_to_json(ws);
        this.import.emit(data as any[]);
        event.target.value = '';
      };
      reader.readAsBinaryString(file);
    } else if (fileExtension === 'docx') {
      const reader: FileReader = new FileReader();
      reader.onload = async (e: any) => {
        try {
          const arrayBuffer = e.target.result;
          const result = await mammoth.extractRawText({ arrayBuffer: arrayBuffer });
          const text = result.value;
          
          const lines = text.split(/\r?\n/).filter((l: string) => l.trim().length > 0);
          const data: any[] = [];
          
          lines.forEach((line: string) => {
             const trimmed = line.trim();
             
             const rangeMatch = trimmed.match(/^([A-Za-z]+[-]?)\s*(\d+)\s*[_\-~]+\s*([A-Za-z]+[-]?\s*)?(\d+)$/);
             
             if (rangeMatch) {
                 const alphabet = rangeMatch[1].trim(); 
                 const startNumStr = rangeMatch[2]; 
                 const endNumStr = rangeMatch[4]; 
                 
                 const startNum = parseInt(startNumStr, 10);
                 const endNum = parseInt(endNumStr, 10);
                 const length = startNumStr.length; 
                 
                 for (let i = startNum; i <= endNum; i++) {
                     const numStr = i.toString().padStart(length, '0');
                     data.push({ alphabet: alphabet, number: numStr });
                 }
             } else {
                 const singleMatch = trimmed.match(/^([A-Za-z]+[-]?)\s*(\d{4,})$/);
                 if (singleMatch) {
                     data.push({ alphabet: singleMatch[1].trim(), number: singleMatch[2] });
                 } 
             }
          });
          
          this.import.emit(data);
        } catch (error) {
          console.error('Error reading docx:', error);
        }
        event.target.value = '';
      };
      reader.readAsArrayBuffer(file);
    } else {
        event.target.value = '';
    }
  }

  get visiblePages(): (number | string)[] {
    const total = this.totalPages;
    const current = this.currentPage;
    const delta = 1; 
    
    if (total <= 7) {
      return Array.from({ length: total }, (_, i) => i + 1);
    }

    const pages: (number | string)[] = [];
    

    pages.push(1);

    if (current > delta + 3) {
      pages.push('...');
    }


    const start = Math.max(2, current - delta);
    const end = Math.min(total - 1, current + delta);

    for (let i = start; i <= end; i++) {
      pages.push(i);
    }

    if (current < total - delta - 2) {
      pages.push('...');
    }


    pages.push(total);

    return pages;
  }

  getCellValue(row: any, key: string) {
    return key.split('.').reduce((obj, i) => obj?.[i], row);
  }

  get totalPages(): number {
    return Math.max(1, Math.ceil(this.totalItems / this.pageSize));
  }
}
